"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Form from "next/form";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { GA4DataLayer, GA4DataLayerFn } from "@packages/lib/utlils/helper-function";
import { SearchFormHandle } from "@packages/lib/types/interfaces";
import { useOutsideClick } from "@packages/hooks/DetectOutsideClick";
import { DataLayerGA4AttrType } from "@packages/lib/types/datalayerGA";
import { GAConstants } from "@packages/shared-components/services/constants";

interface UniversityTabProps {
  customGA?: DataLayerGA4AttrType;
  searchFormHandle: any;
  setsearchFormHandle: any;
  data: any[];
  placeholder: any;
  gaEventName?: string;
  isClearingEnabled?: boolean;
}

const UniversityTab: React.FC<UniversityTabProps> = ({
  customGA,
  searchFormHandle,
  setsearchFormHandle,
  data,
  placeholder,
  gaEventName,
  isClearingEnabled = false,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [dropdownIndex, setdropdownIndex] = useState<number>(0);
  const [universityList, setUniversityList] = useState<any>([]);
  const [unierror, setUnierror] = useState(false);
  const [unidetails, setUnidetails] = useState<Array<any>>(
    Array.isArray(data) ? data : []
  );

  useEffect(() => {
    if (isClearingPage()) {
      let clearingUnis = data?.filter((uni: any) => uni?.clearingInstFlag);
      clearingUnis = clearingUnis?.length ? clearingUnis : data;
      setUnidetails(() => clearingUnis);
    }
  }, [pathname]);

  function isClearingPage(): boolean {
    const clearingQueryParam = searchParams.get("clearing");
    return (
      pathname?.toLowerCase()?.includes("clearing") ||
      clearingQueryParam !== null
    );
  }

  // Use to detect outside click
  const containerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(containerRef, () => courseActions(""));

  // ============================================use effect to filter uninversity==========================================================================================================
  useEffect(() => {
    if (
      !searchFormHandle?.university?.trim() ||
      searchFormHandle?.university?.trim().length < 3
    ) {
      setUniversityList([]);
      return;
    }

    const normalizeText = (text: string) => 
      text?.toLowerCase()?.replace(/['']|&#39;|&amp;#39;/g, '');  
    const searchTerm = normalizeText(searchFormHandle?.university?.trim());
    const results = unidetails?.filter(
      (colleges: any) =>
        normalizeText(colleges?.collegeNameDisplay)?.includes(searchTerm) ||
        normalizeText(colleges?.collegeNameAlias)?.includes(searchTerm)
    );

    const prioritySearch = (
      list: { description: string; [key: string]: any }[],
      searchText: string
    ) => {
      if (!searchText) return list;
      const searchLower = searchText?.toLowerCase();
      return list
        ?.map((item) => ({
          ...item,
          position: item?.description?.toLowerCase().indexOf(searchLower),
          startsWithSearch: item?.collegeNameDisplay
            ?.toLowerCase()
            ?.startsWith(searchLower),
          exactMatch: item?.collegeNameDisplay?.toLowerCase() === searchLower,
        }))
        ?.filter((item) => item.position !== -1) // Only include items with searchText
        ?.sort((a: any, b: any) => {
          if (a?.exactMatch !== b?.exactMatch) return a?.exactMatch ? -1 : 1;
          if (a?.startsWithSearch !== b?.startsWithSearch)
            return a?.startsWithSearch ? -1 : 1;
          if (a?.position !== b?.position) return a.position - b.position;
          return a?.collegeNameDisplay?.localeCompare(b?.collegeNameDisplay);
        })
        ?.map((item: any) => ({
          collegeId: item?.collegeId,
          collegeNameDisplay: item?.collegeNameDisplay,
          collegeNameAlias: item?.collegeNameAlias,
          collegeName: item?.collegeName,
          college_text_key: item?.college_text_key,
        }));
    };

    setUniversityList(
      () => prioritySearch(results, searchFormHandle.university) || []
    );
  }, [searchFormHandle?.university]);

  const resetAllTabs = (currentTab: string) => ({
    isUniversityClicked:
      currentTab === "University"
        ? !searchFormHandle?.isUniversityClicked
        : false,
  });

  const courseActions = (tabName: string) => {
    setsearchFormHandle((prevData: SearchFormHandle) => ({
      ...prevData,
      ...resetAllTabs(tabName),
    }));
  };

  function handleUnisearch() {
    setUnierror(true);
  }

  const handleDropdownArrowFunctionality = (e: any) => {
    if (!searchFormHandle?.isUniversityClicked) return;
    const allOptions: any = universityList || [];
    const currentIndex = dropdownIndex;
    let newIndex = currentIndex;
    let newElement: HTMLElement | null = null;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        newIndex = currentIndex < allOptions.length - 1 ? currentIndex + 1 : 0;
        setdropdownIndex(newIndex);
        newElement = document.querySelector(`[data-index="${newIndex}"]`);
        break;

      case "ArrowUp":
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : allOptions.length - 1;
        setdropdownIndex(newIndex);
        newElement = document.querySelector(`[data-index="${newIndex}"]`);
        break;

      case "Enter":
        e.preventDefault();
        if (dropdownIndex) {
          setsearchFormHandle((prevData: SearchFormHandle) => ({
            ...prevData,
            university: universityList[dropdownIndex - 1]?.collegeNameDisplay,
            isUniversityClicked: false,
          }));
          router.push(
            `/university-profile/${
              universityList[dropdownIndex - 1]?.college_text_key
            }/${universityList[dropdownIndex - 1]?.collegeId}/${isClearingEnabled && isClearingPage() ? "clearing/" : ""}`
          );
        }
        break;
    }
    document
      .querySelector(`[data-index="${currentIndex}"]`)
      ?.classList.remove("bg-blue-50", "underline");
    newElement?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    newElement?.classList.add("bg-blue-50", "underline");
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-[16px]">
      <div className="bg-white rounded-[24px] p-[16px] border border-grey-200 hover:border-primary-500 shadow-custom-1 md:rounded-[32px] md:pl-[24px] md:p-[10px]">
        <Form
          action={handleUnisearch}
          className="flex flex-col gap-[16px] small md:flex-row"
        >
          <div className="relative grow">
            <input
              autoComplete="off"
              onClick={() => {
                courseActions("University");
                setUnierror(false);
              }}
              type="text"
              data-text-id="university_search_input"
              className="w-full focus:outline-none pt-0 pb-[16px] text-black placeholder:text-gray-500 border-b border-grey-200 md:py-[10px] md:border-none"
              aria-label=""
              placeholder={placeholder || "University name"}
              onChange={(event) => {
                setsearchFormHandle((preData: any) => ({
                  ...preData,
                  university: event.target.value
                    .replace(/\s{2,}/g, " ")
                    .trimStart(),
                  isUniversityClicked: true,
                }));
                setUnierror(false);
              }}
              value={searchFormHandle?.university || ""}
              onKeyDown={handleDropdownArrowFunctionality}
            />
            {searchFormHandle?.isUniversityClicked &&
              searchFormHandle?.university?.trim().length > 2 && (
                <div className="flex flex-col w-[calc(100%+16px)] absolute z-[1] bg-white shadow-custom-3 rounded-[8px] left-[-8px] top-[53px] custom-scrollbar-2 max-h-[205px] overflow-y-auto mr-[4px]">
                  <ul>
                    {universityList?.map((item: any, index: any) => (
                      <li key={"university_" + index} data-index={index + 1}>
                        <a
                          role="link"
                          href={`/university-profile/${item?.college_text_key?.trim() ?? ""}/${item?.collegeId ?? ""}/${isClearingEnabled && isClearingPage() ? "clearing/" : ""}`}
                          onClick={async () => {
                            // Update state
                            setsearchFormHandle((prevData: any) => ({
                              ...prevData,
                              university: item.collegeNameDisplay,
                              isUniversityClicked: false,
                            }));

                            // Trigger GA4DataLayerFn
                            GA4DataLayer({
                              event: GAConstants.track_event,
                              event_name:"search_event",
                              search_type: "drop_down",
                              institution_name: item?.collegeNameDisplay,
                              institution_id: item?.collegeId,
                            });
                            GA4DataLayerFn({
                              event: "ga_events",
                              eventName:
                                customGA?.eventName ??
                                gaEventName ??
                                "homepage_search",
                              data_label: `${isClearingEnabled ? "clearing_" : ""}university_search`,
                              college_name: item?.collegeNameDisplay,
                              college_id: item?.collegeId,
                              clearing: isClearingEnabled
                                ? "clearing"
                                : "in-year",
                              page_name:
                                localStorage?.getItem("gaPageName") || "",
                            });
                          }}
                          className="px-[16px] py-[10px] block small hover:bg-blue-50 hover:underline cursor-pointer"
                        >
                          {item?.collegeNameDisplay}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
          <button
            type="submit"
            data-text-id="university_search_button"
            className="btn btn-primary flex items-center justify-center gap-[6px] px-[24px] py-[10px] md:w-[114px]"
          >
            <Image
              src="/static/assets/icons/search_icon.svg"
              width="18"
              height="18"
              alt="Search icon"
            />
            Search
          </button>
        </Form>
      </div>
      {unierror && (
        <p className="small text-negative-default">
          Please select university from dropdown
        </p>
      )}
    </div>
  );
};

export default UniversityTab;
